import { worldRoot } from './worldRoot'
/**
 * Live workflow galleries — wall-mounted boards in the authoritative L1
 * operations-gallery and L2 gallery-mezzanine zones. The latest
 * GALLERY_SLOTS runs of ALL statuses are shown, newest first.
 *
 * selectGalleryRuns — pure newest-first selection, exported for tests.
 *
 * GallerySystem — builds the 24 board entities (thin charcoal frame +
 * status-colored inner panel), one sign per gallery zone, and keeps
 * board→run assignments in sync with the store.
 * DOM mini-cards and [E] handling live in the labels system (labels.ts),
 * which consumes getBoards().
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { NormalizedRun } from '../../api/types'
import { FLOOR_Y, HQ_PROGRAM_ZONES, type HqProgramZone } from './floorPrograms'
import { PALETTE } from './dressing'
import { STATUS_COLORS } from './stations'

export const GALLERY_BOARD_W = 1.15
export const GALLERY_BOARD_H = 0.8
export const GALLERY_L1_SLOTS = 18
export const GALLERY_L2_SLOTS = 6
export const GALLERY_SLOTS = GALLERY_L1_SLOTS + GALLERY_L2_SLOTS

export interface GallerySlotPlacement {
  floor: 1 | 2
  zoneId: 'l1-gallery' | 'l2-gallery'
  position: readonly [number, number, number]
  rotY: number
}

interface GalleryZoneLayout {
  zone: HqProgramZone
  floor: 1 | 2
  columns: number
  rotY: number
  rowHeights: readonly number[]
}

function galleryZone(id: 'l1-gallery' | 'l2-gallery', floor: 1 | 2): HqProgramZone {
  const zone = HQ_PROGRAM_ZONES.find((candidate) => candidate.id === id)
  if (!zone || zone.floor !== floor || zone.kind !== 'gallery') {
    throw new Error(`Missing authoritative ${id} floor-program zone`)
  }
  return zone
}

const GALLERY_ZONE_LAYOUTS: readonly GalleryZoneLayout[] = [
  {
    zone: galleryZone('l1-gallery', 1),
    floor: 1,
    columns: 9,
    rotY: 0,
    rowHeights: [2.15, 1.2],
  },
  {
    zone: galleryZone('l2-gallery', 2),
    floor: 2,
    columns: 6,
    rotY: 90,
    rowHeights: [1.45],
  },
]

/**
 * Deterministic slot placement derived from the authoritative floor-program
 * zones. L1 receives the 18-board operations wall; L2 receives the six-panel
 * mezzanine run. A 90° L2 rotation places its width along the void edge.
 */
export function gallerySlotPlacement(slot: number): GallerySlotPlacement {
  const normalizedSlot = ((slot % GALLERY_SLOTS) + GALLERY_SLOTS) % GALLERY_SLOTS
  const onL1 = normalizedSlot < GALLERY_L1_SLOTS
  const layout = GALLERY_ZONE_LAYOUTS[onL1 ? 0 : 1]
  const localSlot = onL1 ? normalizedSlot : normalizedSlot - GALLERY_L1_SLOTS
  const row = Math.floor(localSlot / layout.columns)
  const column = localSlot % layout.columns
  const { minX, maxX, minZ, maxZ } = layout.zone.bounds
  const alongX = layout.rotY === 0
  const axisMin = alongX ? minX : minZ
  const axisMax = alongX ? maxX : maxZ
  const axisPosition = axisMin + (column + 0.5) * ((axisMax - axisMin) / layout.columns)
  const x = alongX ? axisPosition : (minX + maxX) / 2
  const z = alongX ? (minZ + maxZ) / 2 : axisPosition
  const y = FLOOR_Y[layout.floor] + layout.rowHeights[row]
  return {
    floor: layout.floor,
    zoneId: layout.zone.id as GallerySlotPlacement['zoneId'],
    position: [x, y, z],
    rotY: layout.rotY,
  }
}

interface GallerySignPlacement {
  zoneId: GallerySlotPlacement['zoneId']
  floor: 1 | 2
  position: readonly [number, number, number]
  rotY: number
}

const GALLERY_SIGNS: readonly GallerySignPlacement[] = GALLERY_ZONE_LAYOUTS.map((layout) => {
  const { minX, maxX, minZ, maxZ } = layout.zone.bounds
  return {
    zoneId: layout.zone.id as GallerySignPlacement['zoneId'],
    floor: layout.floor,
    position: [
      (minX + maxX) / 2,
      FLOOR_Y[layout.floor] + (layout.floor === 1 ? 2.78 : 2.18),
      (minZ + maxZ) / 2,
    ],
    rotY: layout.rotY,
  }
})

/** A board currently displaying a run — consumed by the labels system. */
export interface GalleryBoard {
  runId: string
  x: number
  y: number
  z: number
}

/**
 * Latest runs for the gallery wall: ALL statuses, newest first (by createdAt,
 * id as a deterministic tiebreak), capped at `cap`. Pure — exported for tests.
 */
export function selectGalleryRuns(
  runs: readonly NormalizedRun[],
  cap: number = GALLERY_SLOTS,
): NormalizedRun[] {
  return [...runs]
    .sort((a, b) => {
      const ta = new Date(a.createdAt).getTime()
      const tb = new Date(b.createdAt).getTime()
      if (tb !== ta) return tb - ta
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    })
    .slice(0, cap)
}

/** Compact relative-time label for a board mini-card ("just now", "5m ago"). */
export function relativeTime(iso: string, nowMs: number = Date.now()): string {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return ''
  const s = Math.max(0, Math.floor((nowMs - t) / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const DEFAULT_PANEL_COLOR: [number, number, number] = STATUS_COLORS.queued

interface BoardEntry {
  root: pc.Entity
  panelMeshInsts: pc.MeshInstance[]
}

export class GallerySystem {
  private app!: pc.Application
  private boardEntries: BoardEntry[] = []
  private boards: GalleryBoard[] = []
  private dressingEntities: pc.Entity[] = []
  private panelMats = new Map<string, pc.StandardMaterial>()
  private frameMat!: pc.StandardMaterial
  private boardBatchGroupId?: number
  private projectionKey: string | null = null
  private unsubscribe?: () => void

  init(app: pc.Application, options: { externalProjection?: boolean } = {}): void {
    this.app = app

    this.frameMat = new pc.StandardMaterial()
    this.frameMat.diffuse = PALETTE.charcoal
    this.frameMat.gloss = 0.45
    this.frameMat.update()

    this.boardBatchGroupId = app.batcher
      ?.addGroup('hq-gallery-boards', false, 64)
      .id

    this.buildBoards()
    this.buildSigns()

    if (!options.externalProjection) this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      if (state.runs !== prev.runs) this.projectRuns(state.runs)
    })
    if (!options.externalProjection) this.projectRuns(useArenaStore.getState().runs)
  }

  /** Boards currently showing a run (world board-centre positions). */
  getBoards(): readonly GalleryBoard[] {
    return this.boards
  }

  /** Assign the newest-first run set to the board slots. */
  /** P7 seam: fill cells from the coordinator-owned run snapshot. */
  projectRuns(runs: readonly NormalizedRun[]): void {
    const display = selectGalleryRuns(runs)
    const projectionKey = display
      .map((run) => `${run.id}\u0000${run.status}`)
      .join('\u0001')
    if (projectionKey === this.projectionKey) return
    this.projectionKey = projectionKey
    const boards: GalleryBoard[] = []
    for (let slot = 0; slot < GALLERY_SLOTS; slot++) {
      const entry = this.boardEntries[slot]
      const run = display[slot]
      if (!run) {
        entry.root.enabled = false
        continue
      }
      entry.root.enabled = true
      const mat = this.getPanelMat(run.status)
      for (const mi of entry.panelMeshInsts) mi.material = mat
      // WORLD position, not the local slot position: the board entity is
      // parented under the interior island (z ~= -1000), so its office-local
      // coords are not its world coords. Labels project these through
      // worldToScreen — handing back local coords put the gallery cards on the
      // district street. The entity transform already carries the island offset.
      const wp = entry.root.getPosition()
      boards.push({ runId: run.id, x: wp.x, y: wp.y, z: wp.z })
    }
    this.boards = boards
    if (this.boardBatchGroupId !== undefined) {
      this.app.batcher?.markGroupDirty(this.boardBatchGroupId)
    }
  }

  private getPanelMat(status: string): pc.StandardMaterial {
    const cached = this.panelMats.get(status)
    if (cached) return cached
    const c = STATUS_COLORS[status] ?? DEFAULT_PANEL_COLOR
    const mat = new pc.StandardMaterial()
    mat.diffuse = new pc.Color(c[0] * 0.25, c[1] * 0.25, c[2] * 0.25)
    mat.emissive = new pc.Color(c[0], c[1], c[2])
    mat.emissiveIntensity = 0.55
    mat.gloss = 0.6
    mat.update()
    this.panelMats.set(status, mat)
    return mat
  }

  private buildBoards(): void {
    for (let slot = 0; slot < GALLERY_SLOTS; slot++) {
      const placement = gallerySlotPlacement(slot)
      const [x, y, z] = placement.position
      const root = new pc.Entity(`gallery-board-${slot}`)
      root.setLocalPosition(x, y, z)
      root.setLocalEulerAngles(0, placement.rotY, 0)

      // Thin charcoal frame, flush against the wall plane.
      const frame = new pc.Entity('frame')
      frame.addComponent('render', { type: 'box' })
      frame.setLocalScale(GALLERY_BOARD_W, GALLERY_BOARD_H, 0.05)
      frame.setLocalPosition(0, 0, 0)
      for (const mi of frame.render?.meshInstances ?? []) mi.material = this.frameMat
      if (frame.render) {
        frame.render.castShadows = false
        if (this.boardBatchGroupId !== undefined) {
          frame.render.batchGroupId = this.boardBatchGroupId
        }
      }
      root.addChild(frame)

      // Status-colored inner panel, slightly proud of the frame (south face).
      const panel = new pc.Entity('panel')
      panel.addComponent('render', { type: 'box' })
      panel.setLocalScale(GALLERY_BOARD_W - 0.12, GALLERY_BOARD_H - 0.12, 0.02)
      panel.setLocalPosition(0, 0, 0.03)
      const panelMeshInsts = panel.render ? [...panel.render.meshInstances] : []
      if (panel.render) {
        panel.render.castShadows = false
        if (this.boardBatchGroupId !== undefined) {
          panel.render.batchGroupId = this.boardBatchGroupId
        }
      }
      root.addChild(panel)

      root.enabled = false
      worldRoot(this.app).addChild(root)
      this.boardEntries.push({ root, panelMeshInsts })
    }
  }

  /** One "GALLERY" sign per live gallery zone. */
  private buildSigns(): void {
    for (const placement of GALLERY_SIGNS) this.buildSign(placement)
  }

  /** Canvas-texture emissive panel; plain emissive fallback in headless mode. */
  private buildSign(placement: GallerySignPlacement): void {
    const [sx, sy, sz] = placement.position
    const sign = new pc.Entity(`gallery-sign-${placement.zoneId}`)
    sign.addComponent('render', { type: 'box' })
    sign.setLocalScale(3.2, 0.6, 0.04)
    sign.setLocalPosition(sx, sy, sz)
    sign.setLocalEulerAngles(0, placement.rotY, 0)

    const mat = new pc.StandardMaterial()
    mat.diffuse = new pc.Color(0.08, 0.09, 0.13)
    mat.emissive = new pc.Color(0.961, 0.922, 0.875) // cream glow fallback
    mat.emissiveIntensity = 0.5

    // Best-effort text texture — falls back to the plain emissive panel when
    // a 2D canvas is unavailable (tests / headless).
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = 96
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#3d405b'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = '#f5ebe0'
        ctx.font = '700 56px Nunito, system-ui, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.letterSpacing = '18px'
        ctx.fillText('GALLERY', canvas.width / 2 + 9, canvas.height / 2 + 4)
        const tex = new pc.Texture(this.app.graphicsDevice, {
          width: canvas.width,
          height: canvas.height,
        })
        tex.setSource(canvas)
        mat.emissiveMap = tex
        mat.emissive = new pc.Color(1, 1, 1)
        mat.emissiveIntensity = 0.9
      }
    } catch { /* keep the plain emissive panel */ }

    mat.update()
    for (const mi of sign.render?.meshInstances ?? []) mi.material = mat
    worldRoot(this.app).addChild(sign)
    this.dressingEntities.push(sign)
  }

  destroy(): void {
    this.unsubscribe?.()
    if (this.boardBatchGroupId !== undefined) {
      this.app.batcher?.removeGroup(this.boardBatchGroupId)
      this.boardBatchGroupId = undefined
    }
    for (const entry of this.boardEntries) entry.root.destroy()
    this.boardEntries = []
    this.boards = []
    for (const e of this.dressingEntities) e.destroy()
    this.dressingEntities = []
    this.panelMats.clear()
    this.projectionKey = null
  }
}
